import { useEffect, useState, useMemo, useCallback } from "react";
import { useTranslator } from "@eo-locale/react";

import {
  Button,
  Flex,
  Field,
  IconButton,
  MdSort,
  Dialog,
  Portal,
  Radio,
  RadioGroup,
  Stack,
  Text,
  useDisclosure
} from "@/storybook-components";
import { scrollIntoView } from "@/lib";
import { useAppSelector } from "@/redux/store/hooks";

const SortFilterContent = ({
  locale,
  action,
  resetPage,
  sortBy,
  sortOrder,
  categoryId,
  search
}: {
  locale: string;
  action: (
    sortBy: string,
    sortOrder: string,
    search?: string,
    categoryId?: string | null
  ) => void;
  resetPage: (page: number) => void;
  sortBy?: string;
  sortOrder?: string;
  categoryId?: string | null;
  search?: string;
}): JSX.Element => {
  const { translate } = useTranslator();
  const { open: isModalOpen, onOpen, onClose } = useDisclosure();

  const { allowCommentsForIdeas, disableVoting, layout } = useAppSelector(
    (state) => state.brainstormers.brainstormer
  );
  const categories = useAppSelector((state) => state.brainstormers.categories);

  const sortTypeList = [
    "newest",
    "oldest",
    ...(disableVoting ? [] : ["mostVotes"])
  ];

  if (allowCommentsForIdeas) {
    sortTypeList.push("mostComments");
  }

  const visibleCategories = useMemo(
    () => categories?.filter((cat) => cat?.visible) || [],
    [categories]
  );

  // Draft state for modal
  const [draftSortValue, setDraftSortValue] = useState<string>("newest");
  const [draftCategoryId, setDraftCategoryId] = useState<string>("");

  // Initialize draft state when modal opens
  useEffect(() => {
    if (isModalOpen) {
      // Determine current sort value from sortParams
      let currentSortValue = "newest";

      if (sortBy === "created_at" && sortOrder === "asc") {
        currentSortValue = "oldest";
      } else if (sortBy === "idea_votes_count") {
        currentSortValue = "mostVotes";
      } else if (sortBy === "comments_count") {
        currentSortValue = "mostComments";
      }

      setDraftSortValue(currentSortValue);
      setDraftCategoryId(categoryId ? String(categoryId) : "");
    }
  }, [isModalOpen, sortBy, sortOrder, categoryId]);

  const handleResetAll = () => {
    setDraftSortValue("newest");
    setDraftCategoryId("");
  };

  const handleApply = async () => {
    let newSortBy = "created_at";
    let newSortOrder = "desc";

    switch (draftSortValue) {
      case "oldest":
        newSortBy = "created_at";
        newSortOrder = "asc";
        break;
      case "mostVotes":
        newSortBy = "idea_votes_count";
        newSortOrder = "desc";
        break;
      case "mostComments":
        newSortBy = "comments_count";
        newSortOrder = "desc";
        break;
      default:
        newSortBy = "created_at";
        newSortOrder = "desc";
    }

    action(newSortBy, newSortOrder, search, draftCategoryId || null);
    resetPage(1);

    if (layout === "BudgetAllocator") scrollIntoView(".ehq-mainContent");
    else scrollIntoView("#ideas-grid");

    onClose();
  };

  const handleOpenChange = useCallback(
    (details: { open: boolean }) => {
      if (!details.open) {
        onClose();
      }
    },
    [onClose]
  );

  return (
    <>
      <IconButton
        aria-label={translate(`${locale}.sortAndFilter`)}
        icon={<MdSort />}
        data-testid="ideas-sort-filter-trigger"
        variant="solid"
        w={{ base: 10, sm: 12, xl: 10 }}
        h={{ base: 10, sm: 12, xl: 10 }}
        padding={2.5}
        justifyContent="center"
        alignItems="center"
        borderRadius="50%"
        bg="white"
        color="gray.900"
        boxShadow="lg"
        _hover={{ bg: "white", color: "gray.900" }}
        _focus={{ bg: "white", color: "gray.900" }}
        onClick={onOpen}
      />

      <Dialog.Root
        open={isModalOpen}
        onOpenChange={handleOpenChange}
        placement="center"
        size={{ base: "full", lg: "xl" }}
        scrollBehavior="inside"
      >
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content
              w={{ base: "full", lg: "448px" }}
              minW={{ base: "288px" }}
              maxW={{ base: "448px" }}
              m={{ base: 0, md: "auto" }}
              minH={{ base: "100dvh", lg: "auto" }}
              h={{ base: "100dvh", lg: "520px" }}
              overflow="hidden"
              borderRadius={{ base: 0, lg: "md" }}
            >
              <Dialog.Header
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flex="none"
                p={6}
              >
                <Dialog.Title fontSize="xl" fontWeight="semibold">
                  {translate(`${locale}.sortAndFilter`)}
                </Dialog.Title>
                <Dialog.CloseTrigger
                  position="static"
                  data-testid="ideas-sort-filter-close"
                />
              </Dialog.Header>
              <Dialog.Body px={6} py={0} flex="1" minH={0} overflowY="auto">
                <Field.Root mb={6}>
                  <Field.Label id="sort-by-label" mb={4} fontWeight="semibold">
                    {translate(`${locale}.sortByLabel`)}
                  </Field.Label>
                  <RadioGroup
                    aria-labelledby="sort-by-label"
                    value={draftSortValue}
                    onValueChange={(details) =>
                      setDraftSortValue(details.value ?? "")
                    }
                  >
                    <Stack gap={4}>
                      {sortTypeList.map((value) => (
                        <Radio
                          key={value}
                          value={value}
                          data-testid={`sort-filter-option-${value}`}
                        >
                          {translate(`${locale}.${value}`)}
                        </Radio>
                      ))}
                    </Stack>
                  </RadioGroup>
                </Field.Root>

                <Field.Root>
                  <Field.Label
                    id="filter-by-category-label"
                    mb={4}
                    fontWeight="semibold"
                  >
                    {translate(`${locale}.filterByCategory`)}
                  </Field.Label>
                  {visibleCategories.length > 0 ? (
                    <RadioGroup
                      aria-labelledby="filter-by-category-label"
                      value={draftCategoryId}
                      onValueChange={(details) =>
                        setDraftCategoryId(details.value ?? "")
                      }
                    >
                      <Stack gap={4}>
                        {visibleCategories.map((category) => (
                          <Radio
                            key={category.id}
                            value={String(category.id)}
                            data-testid={`category-filter-option-${category.id}`}
                          >
                            {category.name}
                          </Radio>
                        ))}
                      </Stack>
                    </RadioGroup>
                  ) : (
                    <Text color="gray.600" fontSize="sm">
                      {translate(`${locale}.noCategoriesToFilter`)}
                    </Text>
                  )}
                </Field.Root>
              </Dialog.Body>

              <Dialog.Footer
                p={6}
                borderTop={{ base: "1px solid", lg: "none" }}
                borderColor="gray.200"
              >
                <Flex
                  justify={{ base: "space-between", lg: "flex-end" }}
                  w="full"
                  gap={4}
                >
                  <Button
                    variant="secondary"
                    onClick={handleResetAll}
                    data-testid="sort-filter-reset"
                    flex={{ base: 1, lg: "none" }}
                  >
                    {translate(`${locale}.resetAll`)}
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleApply}
                    data-testid="sort-filter-apply"
                    flex={{ base: 1, lg: "none" }}
                  >
                    {translate(`${locale}.apply`)}
                  </Button>
                </Flex>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </>
  );
};

export default SortFilterContent;
